<template>
	<footer ref="el" data-component="footer">
		<div ref="shaderRef" class="shader-container" data-shader="gold-footer" />

		<div class="background"></div>

		<div class="content g-row">
			<h1 class="-title-1 title xxl-12 sm-16">{{ title }}</h1>
			<p class="-text-1 description xxl-8 sm-14">{{ description }}</p>
			<div class="shape" @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave">
				<div class="cta-wrapper xxl-8 lg-8 sm-10">
					<span class="-title-3">{{ ctaTitle }}</span>
					<ButtonSimple :label="cta.buttonLabel" :link="cta.to" />
				</div>
			</div>
		</div>
		<div class="made">
			<nuxt-link to="https://burocratik.com" target="_blank" rel="external">
				<span class="-text-4">MADE BY BÜRO</span>
			</nuxt-link>
		</div>
	</footer>
</template>

<script setup>
import { gsap } from 'gsap'
import { ButtonSimple } from '~/components/primitives'
import { useEmitter } from '~/composables/core'

import { useScreen } from 'vue-screen'

const props = defineProps({
	title: { type: String, default: undefined },
	description: { type: String, default: undefined },
	ctaTitle: { type: String, default: undefined },
	cta: { type: Object, default: undefined },
})

const el = ref(null)
const shaderRef = ref(null)
const context = inject('pageContext')
const emitter = useEmitter()

const screen = useScreen()

const handleMouseEnter = () => {
	if (screen.touch) return

	emitter.emit('shaderCanvas-mouseEnter', shaderRef.value.dataset.shader)

	const background = el.value?.querySelector('.background')
	const ctaWrapper = el.value?.querySelector('.cta-wrapper')

	if (background) {
		gsap.to(background, {
			'--mask-scale': 1.02,
			'--mask-translate-y': 0,
			duration: 1.0,
			ease: 'power4.out',
		})
	}

	if (ctaWrapper) {
		gsap.to(ctaWrapper, {
			'--gap-scale': 1.1,
			duration: 1.0,
			ease: 'power4.out',
		})
	}
}
const handleMouseLeave = () => {
	if (screen.touch) return

	emitter.emit('shaderCanvas-mouseLeave', shaderRef.value.dataset.shader)

	const background = el.value?.querySelector('.background')
	const ctaWrapper = el.value?.querySelector('.cta-wrapper')

	if (background) {
		gsap.to(background, {
			'--mask-scale': 1,
			'--mask-translate-y': 0,
			duration: 1.0,
			ease: 'power2.out',
		})
	}

	if (ctaWrapper) {
		gsap.to(ctaWrapper, {
			'--gap-scale': 1,
			duration: 1.0,
			ease: 'power2.out',
		})
	}
}

const setup = () => {}
const init = () => {}
const kill = () => {}

onBeforeUnmount(() => {})
onMounted(() => {})
</script>

<style lang="scss" scoped>
[data-component='footer'] {
	position: relative;
	display: flex;

	justify-content: center;
	align-items: center;

	width: 100%;

	padding-top: rs(120, 120);
	padding-bottom: rs(82, 82);

	@include max-width($sm) {
		padding-top: rs(72, 72);
	}

	.background {
		$shape-height: rs(500, 210);
		$bottom-offset: rs(82, 82);

		--mask-scale: 1;
		--mask-translate-y: 0;
		--shape-height: #{$shape-height};
		--bottom-offset: #{$bottom-offset};

		position: absolute;
		inset: 0;
		background: var(--color-geral-dark-brown);
		mask-image: linear-gradient(#000 0 0), url('/imgs/shape.svg');
		mask-size: 100%, auto calc(var(--shape-height) * var(--mask-scale));
		mask-repeat: no-repeat;
		mask-position: 0 0, center calc(100% - var(--bottom-offset) - var(--shape-height) / 2 + calc(var(--shape-height) * var(--mask-scale) / 2) + var(--mask-translate-y) * 1px);
		mask-composite: exclude;
		-webkit-mask-composite: xor;
		pointer-events: none;
	}

	.content {
		text-align: center;
		white-space: pre-line;

		display: flex;
		align-items: center;
		flex-direction: column;
		gap: rs(48, 48);

		z-index: 1;

		.title {
			word-wrap: break-word;
		}

		.description {
			opacity: 0.5;
		}

		.shape {
			position: relative;
			height: rs(500, 210);
			width: 100%;
			max-width: rs(700, 290);

			display: flex;
			align-items: center;
			justify-content: center;

			.cta-wrapper {
				display: flex;
				align-items: center;
				flex-direction: column;

				--gap-scale: 1;
				--base-gap: #{rs(48, 48)};

				@include max-width($sm) {
					--base-gap: #{rs(24, 24)};
				}

				& {
					gap: calc(var(--base-gap) * var(--gap-scale));
				}
			}
		}
	}

	.shader-container {
		position: absolute;

		width: 100%;
		height: rs(600, 310);
		margin: 0 auto;

		bottom: rs(32, 32);
	}

	.made {
		position: absolute;

		bottom: rs(32, 32);
		margin: 0 auto;

		:deep(span) {
			position: relative;

			@include extend-hitbox(15);

			&::after {
				content: '';
				position: absolute;
				bottom: -4px;
				left: 0;
				width: 0;
				height: 1px;
				background-color: var(--color-geral-creme);
				transition: width 0.3s ease(out-quint);
			}

			@media (hover: hover) {
				&:hover::after {
					width: 100%;
				}
			}
		}
	}
}
</style>
